import { H } from "@ecco/ui/a11y-heading";
import { useEffect, useState, type ComponentProps, type FC } from "react";
import styles from "./MotionTitle.module.css";

type MotionTitleProps = {
    title: string;
    handleAnimationComplete?: () => void;
    isFirstOnPage?: boolean;
} & Omit<ComponentProps<typeof H>, "children">;

export const MotionTitle: FC<MotionTitleProps> = ({
    title,
    handleAnimationComplete,
    isFirstOnPage,
    ...rest
}) => {
    const [hasMounted, setHasMounted] = useState(false);
    const words = title.split(" ");
    const lastIndex = Array.from(title.trimEnd()).length - 1;
    let index = 0;

    useEffect(() => {
        setHasMounted(true);

        if (lastIndex < 0 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            handleAnimationComplete?.();
        }
    }, [handleAnimationComplete, lastIndex]);

    return (
        <H
            size="h1"
            color="white"
            fontWeight="bold"
            fontSize={["mobileH1", "desktopH1", "desktopH1"]}
            aria-label={title}
            level={isFirstOnPage ? "h1" : null}
            suppressHydrationWarning
            className={hasMounted ? styles.animate : undefined}
            {...rest}
        >
            {words.map((word, wordIndex) => (
                <span className={styles.word} key={wordIndex}>
                    {Array.from(wordIndex < words.length - 1 ? `${word}\u00a0` : word).map(
                        (character) => {
                            const characterIndex = index++;

                            return (
                                <span
                                    className={styles.character}
                                    key={characterIndex}
                                    style={{
                                        animationDelay: `${characterIndex * 0.05}s`,
                                    }}
                                    onAnimationEnd={
                                        characterIndex === lastIndex
                                            ? handleAnimationComplete
                                            : undefined
                                    }
                                >
                                    {character}
                                </span>
                            );
                        }
                    )}
                </span>
            ))}
        </H>
    );
};
